import type { Activity } from './activity';
import type { Address } from './address';
import type { RegisteredCompany } from './company';
import type { ExclusionSegmentSpecific } from './exclusions';
import type { PastClaim } from './pastClaim';
import type { PropertyConditions } from './property';
import type { Representative } from './representative';
import type { IsoDate } from '@insify/global-date';
import type { MoneyField } from '@insify/global-money';
import type {
  ActiveFrenchActivitySpecificField,
  ActiveFrenchActivitySpecificFields,
} from '@insify/shared-company-policy-model';

export const PARAMS_KEY = 'quote-params';
export const CONDITIONS_KEY = 'conditions';
export const ACTIVITIES_KEY = 'activities';
export const COMPANY_SEARCH_KEY = 'search-company';
export const PRICING_KEY = 'total-premium';
export const HIGH_CARE_CONDITIONS_QUERY_KEY = 'high-care-conditions';
export const STRIPE_INTENT_KEY = 'payment-intent'; // covering both scenarios with setupIntent & paymentIntent
export const PROSPECT_STATUS_QUERY_KEY = 'prospect-status';
export const CHECKOUT_KEY = 'checkout';

// Would like to stick to a single query for all user params
export type QueryKeys =
  | typeof PARAMS_KEY
  | typeof CONDITIONS_KEY
  | typeof PRICING_KEY
  | typeof HIGH_CARE_CONDITIONS_QUERY_KEY
  | typeof STRIPE_INTENT_KEY
  | typeof CHECKOUT_KEY
  | typeof PROSPECT_STATUS_QUERY_KEY;

// TODO reuse me from the backend
export type Params = {
  companyRegistrationStatus: 'registered' | 'not-registered';
  company: RegisteredCompany;
  requestCompanyDetails: boolean;
  activitySpecificFieldAnswers: ActiveFrenchActivitySpecificFields;
  companyName: string;
  headCount: number;
  annualRevenue: MoneyField;
  activities: Activity[];
  deductible: MoneyField;
  professionalLiability: { coverLimit: MoneyField };
  publicLiability: { coverLimit: MoneyField };
  legalProtection: boolean;
  representative: Representative;
  address: Address;
  pastClaimsConfirmed: boolean;
  pastClaimsSumBelowThresholdConfirmed: boolean;
  pastInsuranceIssues: boolean;
  pastClaims: PastClaim[];
  propertyDeclarations: boolean;
  paymentMethod: PaymentMethod;
  activeClaims: boolean;
  segmentSpecificExclusions: boolean;
  declarations: string[];
  foundingDate?: IsoDate;
  termsAndConditionsConsent: boolean;
  startDate?: IsoDate;
  carInsurance?: { interested: boolean };
  howDidYouHearAboutUs?: string;
};

export type ActivitySpecificFieldDescription = {
  key: ActiveFrenchActivitySpecificField;
  text: string;
  detailedText?: string;
};

export type RiskCarrier = 'wakam' | 'munichRe' | 'hiscox' | 'axeria';

export type Product =
  | 'hiscoxRcProMisc'
  | 'hiscoxRcProTech'
  | 'hiscoxRcProMac'
  | 'hiscoxRcProMan'
  | 'hiscoxRcProTag'
  | 'hiscoxRcProSecu'
  | 'hiscoxRcProMiscBienEtre'
  | 'hiscoxRcProMedia';

export type Declarations =
  | 'activeClaimAwareness'
  | 'hiscoxFrenchCompany'
  | 'hiscoxNoDoubleInsurance'
  | 'hiscoxNoPastOrPotentialClaims'
  | 'hiscoxSecuritySpecific'
  | 'hiscoxTourismSpecific';

export const paymentMethods = ['sepa', 'creditCard'] as const;
export type PaymentMethod = (typeof paymentMethods)[number];
export type GhostCardSlug = 'legalProtection' | 'cyber' | 'property' | 'dnoLiability' | 'carInsurance';

export type Conditions = {
  legalStructureCodes: { description: string; value: string }[];
  activitySpecificFieldDescriptions: ActivitySpecificFieldDescription[];
  pastClaimsThreshold: MoneyField;
  pastClaimsImpactsPremium: boolean;
  foundingDateImpactsPremium: boolean;
  exclusions: ExclusionSegmentSpecific[];
  companyNameEditable: boolean;
  deductibleOptions: MoneyField[];
  deductibleOptionsRCExp: MoneyField[];
  riskCarriers: RiskCarrier[];
  professionalLiability: {
    coverLimitOptions: MoneyField[];
    popularCoverLimitOption: MoneyField;
    dailyCancellable: boolean;
  };
  popularDeductibleOption: MoneyField;
  paymentMethods: PaymentMethod[];
  ghostCards: GhostCardSlug[];
  skipSteps: Array<'headcount' | 'past-claims' | 'past-insurance'>;
  declarations: Declarations[];
  startDateBoundariesInDays: [number, number];
  products: Product[];
} & PropertyConditions;

export type PaymentWidgetData = {
  intentType: 'setup-intent' | 'payment-intent';
  stripeClientSecret: string;
  status: string;
};

export type ProspectStatusResponse = {
  orderStatus?: 'lead' | 'applied';
  versionStatus?: 'up-to-date' | 'outdated';
  quoteStatus?: 'not-quoted' | 'quoted' | 'quote-outdated';
};
